/**
 * ImageCacheManager - Singleton for managing image preloading cache
 *
 * Centralizes all cache state management to avoid race conditions
 * and unintended sharing between multiple component instances.
 *
 * Features:
 * - Singleton pattern ensures single cache instance
 * - LRU eviction when cache exceeds limit
 * - Bulk preload with progress tracking
 * - Explicit reset capability for testing
 */

interface CacheEntry {
  img: HTMLImageElement;
  loaded: boolean;
  lastAccess: number;
}

class ImageCacheManager {
  private static instance: ImageCacheManager;

  private cache = new Map<string, CacheEntry>();
  private bulkPreloadCompleted = false;
  private bulkPreloadTargetSize = 0;
  private bulkPreloadAborted = false;
  private preloadInProgress = new Set<string>();

  readonly MAX_CACHE_SIZE = 1000;
  readonly BASE_BUFFER_SIZE = 150;
  readonly MAX_BUFFER_SIZE = 400;
  readonly BULK_CONCURRENCY = 20;

  /**
   * Get the singleton instance
   */
  static getInstance(): ImageCacheManager {
    if (!ImageCacheManager.instance) {
      ImageCacheManager.instance = new ImageCacheManager();
    }
    return ImageCacheManager.instance;
  }

  /**
   * Private constructor - use getInstance()
   */
  private constructor() {}

  /**
   * Check if an image URL is cached and loaded
   */
  isImageCached(url: string): boolean {
    const entry = this.cache.get(url);
    if (entry) {
      // Only update lastAccess if LRU eviction is still active
      if (!this.bulkPreloadCompleted) {
        entry.lastAccess = Date.now();
      }
      return entry.loaded;
    }
    return false;
  }

  /**
   * Preload a single image
   */
  preloadImage(url: string, onLoaded?: () => void): void {
    if (!url) return;

    // Skip if already cached or loading
    if (this.cache.has(url) || this.preloadInProgress.has(url)) {
      return;
    }

    // Mark as in progress
    this.preloadInProgress.add(url);

    // Create new image element
    const img = new Image();

    // Create cache entry immediately (loading state)
    const entry: CacheEntry = {
      img,
      loaded: false,
      lastAccess: Date.now(),
    };
    this.cache.set(url, entry);

    img.onload = () => {
      entry.loaded = true;
      this.preloadInProgress.delete(url);
      onLoaded?.();
    };

    img.onerror = () => {
      // Remove failed entry from cache
      this.cache.delete(url);
      this.preloadInProgress.delete(url);
      onLoaded?.();
    };

    // Start loading
    img.src = url;

    // Evict if needed
    this.evictLRU();
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { total: number; loaded: number; loading: number } {
    let loaded = 0;
    let loading = 0;

    this.cache.forEach((entry) => {
      if (entry.loaded) {
        loaded++;
      } else {
        loading++;
      }
    });

    return { total: this.cache.size, loaded, loading };
  }

  /**
   * Get current cache size
   */
  getCacheSize(): number {
    return this.cache.size;
  }

  /**
   * Check if bulk preload has completed
   */
  isBulkPreloadCompleted(): boolean {
    return this.bulkPreloadCompleted;
  }

  /**
   * Cancel any ongoing bulk preload
   */
  cancelBulkPreload(): void {
    this.bulkPreloadAborted = true;
  }

  /**
   * Bulk preload multiple images with progress callback
   */
  async bulkPreload(
    urls: string[],
    onProgress?: (loaded: number, total: number) => void
  ): Promise<void> {
    this.bulkPreloadAborted = false;
    this.bulkPreloadCompleted = false;
    this.bulkPreloadTargetSize = urls.length;

    // Filter out already cached URLs
    const urlsToLoad = urls.filter((url) => {
      if (!url) return false;
      const entry = this.cache.get(url);
      return !entry || !entry.loaded;
    });

    const total = urls.length;
    let loaded = total - urlsToLoad.length; // Already cached count

    // Report initial progress
    onProgress?.(loaded, total);

    if (urlsToLoad.length === 0) {
      this.bulkPreloadCompleted = true;
      return;
    }

    let activeCount = 0;
    let urlIndex = 0;

    const loadNextImage = (): Promise<void> => {
      return new Promise((resolve) => {
        if (urlIndex >= urlsToLoad.length || this.bulkPreloadAborted) {
          resolve();
          return;
        }

        const url = urlsToLoad[urlIndex++];
        activeCount++;

        // Skip if already cached
        if (this.cache.has(url) && this.cache.get(url)!.loaded) {
          loaded++;
          onProgress?.(loaded, total);
          activeCount--;
          resolve();
          return;
        }

        const img = new Image();
        const entry: CacheEntry = {
          img,
          loaded: false,
          lastAccess: Date.now(),
        };
        this.cache.set(url, entry);

        const onComplete = () => {
          entry.loaded = true;
          loaded++;
          activeCount--;
          onProgress?.(loaded, total);
          resolve();
        };

        const onError = () => {
          this.cache.delete(url);
          loaded++;
          activeCount--;
          onProgress?.(loaded, total);
          resolve();
        };

        img.onload = () => {
          img.decode().then(onComplete).catch(onComplete);
        };
        img.onerror = onError;
        img.src = url;
      });
    };

    // Load with concurrency limit
    const loadLoop = async () => {
      while (urlIndex < urlsToLoad.length && !this.bulkPreloadAborted) {
        while (activeCount < this.BULK_CONCURRENCY && urlIndex < urlsToLoad.length) {
          loadNextImage();
        }
        await new Promise((r) => setTimeout(r, 10));
      }

      // Wait for remaining active loads
      while (activeCount > 0) {
        await new Promise((r) => setTimeout(r, 10));
      }
    };

    await loadLoop();

    if (!this.bulkPreloadAborted) {
      this.bulkPreloadCompleted = true;
    }
  }

  /**
   * Evict least recently used entries when cache exceeds max size
   */
  private evictLRU(targetSize: number = this.MAX_CACHE_SIZE): void {
    const effectiveTarget = Math.max(targetSize, this.bulkPreloadTargetSize);
    if (this.cache.size <= effectiveTarget) return;

    // Don't evict if bulk preload completed
    if (this.bulkPreloadCompleted) return;

    const entries = Array.from(this.cache.entries()).sort(
      ([, a], [, b]) => a.lastAccess - b.lastAccess
    );

    const toRemove = this.cache.size - effectiveTarget;
    for (let i = 0; i < toRemove; i++) {
      const [url] = entries[i];
      this.cache.delete(url);
    }
  }

  /**
   * Reset cache state (for testing or cleanup)
   */
  reset(): void {
    this.cache.clear();
    this.bulkPreloadCompleted = false;
    this.bulkPreloadTargetSize = 0;
    this.bulkPreloadAborted = false;
    this.preloadInProgress.clear();
  }
}

// Export singleton instance
export const cacheManager = ImageCacheManager.getInstance();

// Export class for typing
export type { ImageCacheManager };
